import styled, { css } from 'styled-components';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { getColor, setPortalLayer } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';

const CHECKBOX_SIZE_REM = 1.25;
const DEFAULT_TEXT_INPUT_HEIGHT_REM = 3.25;
const INPUT_DISABLED_DATA_ATTRIBUTE = "data-hpy-web-input-disabled";
const INPUT_READ_ONLY_DATA_ATTRIBUTE = "data-hpy-web-input-read-only";
/**
 * Default input stylings.
 * (This same style is used across all 'extended' inputs - text, textarea, select etc.)
 */ const CSSDisabledInput = css`
  cursor: default;
  pointer-events: none;
  color: ${COLOR_PALETTE.slateGrey20};
`;
const CSSReadOnlyInput = css`
  cursor: default;
  pointer-events: none;
  background-color: ${COLOR_PALETTE.transparent};
`;
const CSSDefaultInput = css`
  color: ${COLOR_PALETTE.slateGrey100};
  background-color: ${COLOR_PALETTE.slateGrey5};
  border: 1px solid transparent;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 300;
  padding: 1rem;
  font-family: inherit;
  min-height: ${DEFAULT_TEXT_INPUT_HEIGHT_REM}rem;

  &::placeholder {
    color: ${COLOR_PALETTE.slateGrey60};
    font-weight: 600;
  }
  &:hover,
  &:focus,
  &:focus-within {
    border-color: ${(props)=>getColor("context", props.theme)};
    outline: none;
  }

  /* replace the default autofill stylings */
  &:-webkit-autofill,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px ${COLOR_PALETTE.slateGrey5} inset;
    border: 1px solid transparent;
  }

  & [data-hpy-web-defaultinput-input-label] {
    margin-top: 0.625rem;
  }

  /* handle 'placeholder' label animation & placement */
  &&[data-hpy-web-text-input-input] {
    padding: 1rem 1rem 0.25rem;
  }

  &&&:placeholder-shown {
    padding: 1rem;
  }

  & ~ [data-hpy-web-text-input-input-inner-label] {
    margin-left: 1px;
    white-space: nowrap;
    overflow: hidden;
    max-width: -webkit-fill-available;
    margin-right: 1rem;
  }

  &[data-hpy-web-select-input-input-has-value] [data-hpy-web-select-input-input-inner-label],
  &[data-hpy-web-defaultinput-input-has-value='true'] [data-hpy-web-defaultinput-input-inner-label],
  & ~ [data-hpy-web-combobox-input-input-inner-label],
  & ~ [data-hpy-web-text-input-input-inner-label] {
    pointer-events: none;
    transition: transform 0.2s ease-in-out;
    opacity: 1;
    transform: scale(0.75) translateX(0) translateY(0);
    transform-origin: top left;
    display: flex;
  }

  & [data-hpy-web-select-input-input-inner-label],
  & [data-hpy-web-defaultinput-input-inner-label],
  &&&:placeholder-shown ~ [data-hpy-web-combobox-input-input-inner-label],
  &&&:placeholder-shown ~ [data-hpy-web-text-input-input-inner-label] {
    transform: scale(1) translateX(0rem) translateY(0.875rem);
    transform-origin: top left;
  }

  &&&:placeholder-shown ~ [data-hpy-web-combobox-input-input-inner-label],
  &&&:placeholder-shown ~ [data-hpy-web-text-input-input-inner-label] {
    opacity: 0;
  }

  & ~ [data-hpy-web-text-input-input-clear-button] {
    position: absolute;
    right: 0.5rem;
    top: 0.45rem;
    background-color: transparent;
    color: ${COLOR_PALETTE.slateGrey40};
    &:hover {
      color: ${COLOR_PALETTE.slateGrey100};
    }
  }

  /* optional icon */
  & [data-hpy-web-defaultinput-input-icon],
  & ~ [data-hpy-web-text-input-input-icon] {
    position: absolute;
    height: 2rem;
    width: 2rem;
    top: 0.5rem;
    left: 0.625rem;
    color: ${COLOR_PALETTE.slateGrey60};
  }

  &[data-hpy-web-defaultinput-input-has-icon='true'],
  [data-hpy-web-text-input-has-icon] > &&[data-hpy-web-text-input-input] {
    padding: 1rem 1rem 0.25rem 3rem;
  }

  [data-hpy-web-text-input-has-icon] > &&&:placeholder-shown {
    padding: 1rem 1rem 1rem 3rem;
  }

  [data-hpy-web-text-input-has-icon] > & ~ [data-hpy-web-text-input-input-inner-label] {
    padding-left: 2.625rem;
  }

  /* input states */
  [${INPUT_DISABLED_DATA_ATTRIBUTE}] > &,
  [${INPUT_DISABLED_DATA_ATTRIBUTE}] & ~ [data-hpy-web-text-input-inner-label],
  [${INPUT_DISABLED_DATA_ATTRIBUTE}] & ~ [data-hpy-web-text-input-icon],
  [${INPUT_DISABLED_DATA_ATTRIBUTE}] &::placeholder {
    pointer-events: none;
    ${CSSDisabledInput}
  }

  &[${INPUT_READ_ONLY_DATA_ATTRIBUTE}] {
    ${CSSReadOnlyInput}
  }
`;
/**
 * Default popover stylings
 */ const CSSDefaultPopover = css`
  background-color: white;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 2px 3px 0px;
  border: 1px solid ${COLOR_PALETTE.slateGrey10};
  border-radius: 0.5rem;
  padding: 0;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 2px 3px 0px;
  outline: none;
  overflow: hidden;
  ${setPortalLayer()}

  &:focus-within {
    box-shadow: rgba(0, 0, 0, 0.16) 0px 2px 3px 0px;
    outline: none;
  }
`;
/**
 * Default menu/dropdown option stylings
 */ const CSSDefaultOption = css`
  height: 2.5rem;
  padding: 0 1rem;
  justify-content: center;
  display: flex;
  flex-direction: column;
  position: relative;

  &[aria-selected='true'],
  &:hover {
    cursor: pointer;

    background: ${COLOR_PALETTE.slateGrey5};
    color: ${(props)=>getColor("context", props.theme)};
  }
`;
/**
 * Displays a 'descriptor' label within an input - used for 'Material UI' style labels.
 */ const InnerInputLabel = styled(Text)`
  color: ${COLOR_PALETTE.slateGrey60};
  position: absolute;
  left: 1rem;
  top: 0.5rem;
  font-weight: 600;
  && {
    margin-top: 0; /* annoying bypass on standard stack behaviour - input needs to be direct child of field data attribute */
  }
`;
const setDisabledDataAttribute = (isDisabled)=>{
    return isDisabled ? {
        [INPUT_DISABLED_DATA_ATTRIBUTE]: ""
    } : {};
};
const setReadOnlyDataAttribute = (isReadOnly)=>{
    return isReadOnly ? {
        [INPUT_READ_ONLY_DATA_ATTRIBUTE]: "",
        "aria-readonly": true
    } : {};
};

export { CHECKBOX_SIZE_REM, CSSDefaultInput, CSSDefaultOption, CSSDefaultPopover, CSSDisabledInput, CSSReadOnlyInput, INPUT_DISABLED_DATA_ATTRIBUTE, INPUT_READ_ONLY_DATA_ATTRIBUTE, InnerInputLabel, setDisabledDataAttribute, setReadOnlyDataAttribute };
